// Money → Overview (spec 044 D100–D112, plan slice 9): every read the page makes. Each is a read
// another page already makes (D111) — Transactions' ledger sums and page, Reports' test accounts
// and day summaries, Payouts' tutors, pay cycle and cycle lessons — plus the month's payouts by
// `createdAt`. The arithmetic is src/utils/financialOverview.js.
import { getLedgerAggregates, getLedgerPage } from "./transactions";
import { collected, getReportStats, readTestLedger } from "./reports";
import { getCycleBookings, getPayoutsBetween, getTutorsForPay } from "./payouts";
import { getPayCycleVersions, getUsersByIds } from "./dataService";
import { cyclePeriod } from "../utils/payCycle";
import { revenueSpan } from "../utils/financialOverview";

// A read that fails is null in the answer (and logged); the page shows "—" for it (D36).
const settle = (what, promise) =>
  promise.catch((e) => {
    console.error(`Financial: ${what} failed:`, e);
    return null;
  });

// D102–D107: everything the chosen month decides, for `windows` (monthWindows). Never rejects.
// The recent rows wait for the test accounts, whose rows they skip (D103, R56).
export const getFinancialMonth = async (windows) => {
  const { soFar } = windows;
  const [ledger, collectedBefore, test, payouts] = await Promise.all([
    getLedgerAggregates({ range: soFar }),
    collected("collected before", soFar.prev),
    settle("the test accounts", readTestLedger({ from: soFar.prev.from, to: soFar.to })),
    settle("the payouts", getPayoutsBetween(soFar)),
  ]);
  const recent = test
    ? await settle(
        "the recent rows",
        getLedgerPage({ tab: "all", range: soFar, pageSize: 5, batch: 25, skipUsers: new Set(test.users.map((u) => u.id)) }).then(
          async (page) => ({ rows: page.rows, people: await getUsersByIds(page.rows.map((r) => r.userId)) })
        )
      )
    : null;
  return { ledger, collectedBefore, test, payouts, recent };
};

// D104, D108: what is owed today — the tutors (Payouts' read; the arithmetic drops test tutors),
// the running pay cycle and its lessons, for each tutor's hours. Rejects if a read fails.
// ponytail: reads the running cycle's lessons, as Payouts does; past a few thousand a cycle,
// keep the hours on the tutor document.
export const getPayoutsDue = async (now = new Date()) => {
  const [tutors, versions] = await Promise.all([getTutorsForPay(), getPayCycleVersions()]);
  const cycle = cyclePeriod(versions, now, 0);
  const bookings = await getCycleBookings(cycle);
  return { tutors, cycle, bookings };
};

// D105, R55: the day summaries for the 12 months ending with `key`, and stats/audience — the mark
// that buildReportStats has run — through Reports' reader. Rejects if a read fails.
export const getRevenueDays = (key, now = new Date()) => getReportStats(revenueSpan(key, now));
